import { Box } from '@mui/material';
import { ThemeProvider } from '@mui/material/styles';
import React from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import MockupBanner from '../MockupBanner';
import mockupTheme from '../mockupTheme';
import { mockCourses, StudentLesson } from '../student/mockData';
import { normalizeDesktopAvatarColor, StudentAvatarColor } from './desktopAvatarColors';
import StudentDesktopShell, { StudentDesktopPage } from './components/StudentDesktopShell';
import StudentDesktopCoursesPage from './page/StudentDesktopCoursesPage';
import StudentDesktopFeedbackPage from './page/StudentDesktopFeedbackPage';
import StudentDesktopHomePage from './page/StudentDesktopHomePage';
import StudentDesktopLessonPlayerPage from './page/StudentDesktopLessonPlayerPage';
import StudentDesktopLessonsPage from './page/StudentDesktopLessonsPage';
import StudentDesktopProfilePage from './page/StudentDesktopProfilePage';
import StudentDesktopPronunciationPage from './page/StudentDesktopPronunciationPage';
import StudentDesktopSettingsPage, { StudentDesktopPreferences } from './page/StudentDesktopSettingsPage';

const basePath = '/mockup/student_desktop';
const pagePaths: Record<StudentDesktopPage, string> = {
    home: basePath,
    courses: `${basePath}/courses`,
    pronunciation: `${basePath}/pronunciation`,
    feedback: `${basePath}/feedback`,
    profile: `${basePath}/profile`,
    settings: `${basePath}/settings`,
};

// The mobile mockup stores grading as relaxed/standard/strict under the same key,
// so desktop translates to and from the production easy/medium/hard levels.
const scoringToMobile: Record<StudentDesktopPreferences['scoring'], string> = {
    easy: 'relaxed',
    medium: 'standard',
    hard: 'strict',
};

function initialPreferences(): StudentDesktopPreferences {
    const teacher = localStorage.getItem('teacher');
    const scoring = localStorage.getItem('studentScoring');
    return {
        teacher: teacher === 'male' || teacher === 'female2' ? teacher : 'female',
        audioSpeed: localStorage.getItem('studentAudioSpeed') === 'fast' ? 'fast' : 'normal',
        reading: localStorage.getItem('studentCharacters') === 'pinyin' ? 'pinyin' : 'characters',
        scoring: scoring === 'relaxed' || scoring === 'easy' ? 'easy' : scoring === 'strict' || scoring === 'hard' ? 'hard' : 'medium',
    };
}

function parsePositiveInteger(value: string | undefined): number | null {
    if (!value || !/^\d+$/.test(value)) {
        return null;
    }
    const parsed = Number(value);
    return Number.isSafeInteger(parsed) && parsed > 0 ? parsed : null;
}

export default function StudentDesktopExperience() {
    const history = useHistory();
    const location = useLocation();
    const [notice, setNotice] = React.useState('');
    const [studentName, setStudentName] = React.useState(() => localStorage.getItem('studentName') || 'Maya Chen');
    const [avatarColor, setAvatarColor] = React.useState(() => normalizeDesktopAvatarColor(localStorage.getItem('studentAvatarColor')));
    const [preferences, setPreferences] = React.useState(initialPreferences);
    const segments = location.pathname.slice(basePath.length).split('/').filter(Boolean);
    const firstSegment = segments[0];
    const courseSegment = segments[1];
    const lessonSegment = segments[2];
    const activePage: StudentDesktopPage = firstSegment === 'courses' || firstSegment === 'pronunciation' || firstSegment === 'feedback' || firstSegment === 'profile' || firstSegment === 'settings'
        ? firstSegment
        : 'home';
    const selectedCourseId = activePage === 'courses' ? parsePositiveInteger(segments[1]) : null;
    const selectedLessonId = activePage === 'courses' && segments[2] === 'lessons' ? parsePositiveInteger(segments[3]) : null;
    const hasExerciseRoute = selectedLessonId != null && segments[4] === 'exercises' && segments.length === 6;
    const selectedExerciseStep = hasExerciseRoute ? parsePositiveInteger(segments[5]) : null;
    const lessonFinished = selectedLessonId != null && segments[4] === 'complete' && segments.length === 5;
    const showEmptyFeedback = activePage === 'feedback' && segments[1] === 'empty' && segments.length === 2;
    const selectedCourse = mockCourses.find(course => course.id === selectedCourseId);
    const selectedLesson = selectedCourse?.lessons.find(lesson => lesson.id === selectedLessonId);
    const validExerciseStep = selectedExerciseStep != null && selectedLesson != null && selectedExerciseStep <= selectedLesson.totalPrompts;
    const validCompletion = lessonFinished && selectedLesson != null && selectedLesson.completedPrompts >= selectedLesson.totalPrompts;

    let canonicalPath: string | null = null;
    if (!firstSegment) {
        canonicalPath = location.pathname === basePath ? null : basePath;
    } else if (firstSegment !== 'courses' && firstSegment !== 'pronunciation' && firstSegment !== 'feedback' && firstSegment !== 'profile' && firstSegment !== 'settings') {
        canonicalPath = basePath;
    } else if (activePage !== 'courses') {
        const validStaticRoute = segments.length === 1 || (activePage === 'feedback' && showEmptyFeedback);
        canonicalPath = validStaticRoute ? null : pagePaths[activePage];
    } else if (!courseSegment) {
        canonicalPath = segments.length === 1 ? null : pagePaths.courses;
    } else if (!selectedCourse) {
        canonicalPath = pagePaths.courses;
    } else {
        const coursePath = `${pagePaths.courses}/${selectedCourse.id}`;
        if (segments.length === 2) {
            canonicalPath = null;
        } else if (lessonSegment !== 'lessons' || !selectedLesson) {
            canonicalPath = coursePath;
        } else {
            const lessonPath = `${coursePath}/lessons/${selectedLesson.id}`;
            const resumeStep = selectedLesson.completedPrompts >= selectedLesson.totalPrompts ? 1 : selectedLesson.completedPrompts + 1;
            const resumePath = `${lessonPath}/exercises/${resumeStep}`;
            canonicalPath = (hasExerciseRoute && validExerciseStep) || validCompletion ? null : resumePath;
        }
    }

    React.useEffect(() => {
        document.getElementById('student-desktop-content')?.scrollTo({ top: 0 });
        if (canonicalPath && canonicalPath !== location.pathname) {
            history.replace(canonicalPath);
        }
    }, [canonicalPath, history, location.pathname]);

    const navigate = (page: StudentDesktopPage) => {
        setNotice('');
        history.push(pagePaths[page]);
    };

    const openCourse = (courseId: number) => {
        setNotice('');
        history.push(`${pagePaths.courses}/${courseId}`);
    };

    const openLesson = (courseId: number, lesson: StudentLesson) => {
        setNotice('');
        const resumeIndex = lesson.completedPrompts >= lesson.totalPrompts ? 0 : lesson.completedPrompts;
        history.push(`${pagePaths.courses}/${courseId}/lessons/${lesson.id}/exercises/${resumeIndex + 1}`);
    };

    const saveProfile = (name: string, color: StudentAvatarColor) => {
        setStudentName(name);
        setAvatarColor(color);
        localStorage.setItem('studentName', name);
        localStorage.setItem('studentAvatarColor', color);
        if (!localStorage.getItem('studentAvatar')) {
            localStorage.setItem('studentAvatar', 'initial');
        }
        setNotice('Profile saved for this prototype session.');
    };

    const savePreferences = (next: StudentDesktopPreferences) => {
        setPreferences(next);
        localStorage.setItem('teacher', next.teacher);
        localStorage.setItem('studentAudioSpeed', next.audioSpeed);
        localStorage.setItem('studentCharacters', next.reading);
        localStorage.setItem('studentScoring', scoringToMobile[next.scoring]);
        setNotice('Lesson settings saved.');
    };

    let content: React.ReactNode;
    switch (activePage) {
        case 'profile':
            content = <StudentDesktopProfilePage studentName={studentName} avatarColor={avatarColor} onSave={saveProfile} onNavigate={navigate} />;
            break;
        case 'settings':
            content = <StudentDesktopSettingsPage preferences={preferences} onSave={savePreferences} />;
            break;
        case 'pronunciation':
            content = <StudentDesktopPronunciationPage onNotice={setNotice} />;
            break;
        case 'feedback':
            content = <StudentDesktopFeedbackPage showEmpty={showEmptyFeedback} />;
            break;
        case 'courses': {
            if (selectedCourse && selectedLesson && (validExerciseStep || validCompletion)) {
                const lessonPath = `${pagePaths.courses}/${selectedCourse.id}/lessons/${selectedLesson.id}`;
                content = (
                    <StudentDesktopLessonPlayerPage
                        key={`${selectedLesson.id}-${lessonFinished ? 'complete' : selectedExerciseStep}`}
                        course={selectedCourse}
                        lesson={selectedLesson}
                        exerciseIndex={validExerciseStep && selectedExerciseStep != null ? selectedExerciseStep - 1 : 0}
                        finished={validCompletion}
                        onExit={() => history.push(`${pagePaths.courses}/${selectedCourse.id}`)}
                        onStep={index => history.push(`${lessonPath}/exercises/${index + 1}`)}
                        onComplete={() => history.push(`${lessonPath}/complete`)}
                        onStartNextLesson={lesson => openLesson(selectedCourse.id, lesson)}
                        onOpenFeedback={() => history.push(pagePaths.feedback)}
                    />
                );
            } else if (selectedCourse) {
                content = <StudentDesktopLessonsPage course={selectedCourse} onBack={() => history.push(pagePaths.courses)} onOpenLesson={lesson => openLesson(selectedCourse.id, lesson)} />;
            } else {
                content = <StudentDesktopCoursesPage onOpenCourse={openCourse} />;
            }
            break;
        }
        default:
            content = <StudentDesktopHomePage studentName={studentName} onNavigate={navigate} onOpenCourse={openCourse} onOpenLesson={openLesson} />;
    }

    return (
        <ThemeProvider theme={mockupTheme}>
            <Box sx={{ height: '100vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                <MockupBanner surface="Student" />
                <StudentDesktopShell
                    activePage={activePage}
                    studentName={studentName}
                    avatarColor={avatarColor}
                    notice={notice}
                    onNavigate={navigate}
                    onNotice={setNotice}
                    onCloseNotice={() => setNotice('')}
                >
                    {content}
                </StudentDesktopShell>
            </Box>
        </ThemeProvider>
    );
}
